/**
 * repositories/subscriptionsRepository.ts
 * Subscriptions table data access layer
 *
 * Provides type-safe data access for subscriptions table with centralized mapping
 * between database snake_case and application camelCase.
 *
 * Project: P07 - Subscription Flow
 * Architecture: Repository Pattern per CODING_STANDARDS.md
 */

import { supabase } from '@/lib/supabase';
import { toDomain } from '@/lib/supabaseMappers';
import type { Subscription, SubscriptionInfo } from '@/types/supabase/Subscription';

/**
 * Subscriptions Repository
 *
 * Provides data access for user subscription records.
 * Subscriptions are managed by Stripe webhooks, so this repository
 * is primarily read-only from the application's perspective.
 */
export const subscriptionsRepository = {
  /**
   * Get subscription by user ID
   *
   * @param userId - User ID (UUID)
   * @returns Promise<Subscription | null> - Subscription if found, null otherwise
   */
  async getByUserId(userId: string): Promise<Subscription | null> {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;

    return toDomain<Subscription>(data);
  },

  /**
   * Get subscription info for display (subset of fields)
   *
   * Optimized query that only fetches display-relevant fields.
   *
   * @param userId - User ID (UUID)
   * @returns Promise<SubscriptionInfo | null> - Subscription info if found, null otherwise
   */
  async getInfoByUserId(userId: string): Promise<SubscriptionInfo | null> {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('id, plan_type, billing_interval, status, current_period_end, cancel_at_period_end')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;

    return toDomain<SubscriptionInfo>(data);
  },

  /**
   * Check if user has an active subscription
   *
   * @param userId - User ID (UUID)
   * @returns Promise<boolean> - True if user has active subscription
   */
  async hasActiveSubscription(userId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('id')
      .eq('user_id', userId)
      .eq('status', 'active')
      .maybeSingle();

    if (error) throw error;
    return data !== null;
  },

  /**
   * Get active or trialing subscription plan type for header badge
   *
   * Returns just the plan_type for subscriptions with active or trialing status.
   * Used for displaying subscription badge in UI header.
   *
   * @param userId - User ID (UUID)
   * @returns Promise<{ planType: string; status: string } | null> - Plan info if active/trialing, null otherwise
   */
  async getActivePlanType(userId: string): Promise<{ planType: string; status: string } | null> {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('plan_type, status')
      .eq('user_id', userId)
      .in('status', ['active', 'trialing'])
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;

    // Use toDomain for consistent mapping
    const mapped = toDomain<{ planType: string; status: string }>(data);
    return mapped;
  }
};
